/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Travel trip-entity list: the generic EntityList fed by tripEntityConfig.
 * What is Travel-specific stays here: the /trip-wizard list endpoint, "Edit in
 * Wizard" (edits happen in the trip wizard, not in place) and the add dialog
 * that picks a package and opens the wizard at the right step.
 */

import { useMemo } from "react";
import PropTypes from "prop-types";
import EditIcon from "@mui/icons-material/Edit";
import { EntityList } from "components/EntityFramework";
import TripAddDialog from "./TripAddDialog";
import { getTripEntityConfig, resolveNumericTripId } from "./tripEntityConfig";

export function wizardEditPath(row, wizardStep) {
    const tripId = resolveNumericTripId(row);
    if (!tripId) return null;
    const packageId = Number(row?.trip_package_id ?? row?.package_id);
    const base = Number.isInteger(packageId) && packageId > 0
        ? `/trips/${tripId}/packages/${packageId}/edit`
        : `/trips/${tripId}/edit`;
    return `${base}?step=${wizardStep}`;
}

export function buildTripListConfig(entityKey) {
    const config = getTripEntityConfig(entityKey);
    if (!config) return null;
    return {
        title: config.title,
        subtitle: config.subtitle,
        addLabel: config.addLabel,
        route: config.route,
        primaryField: config.primaryField,
        objectType: config.objectType,
        supportsDelete: config.supportsDelete,
        detailApiPath: config.detailApiPath,
        listApiPath: `/trip-wizard/lists/${config.listKey}`,
        pagination: "client",
        columns: config.columns,
        rowActions: (row, { canEdit, navigate }) => {
            const path = canEdit ? wizardEditPath(row, config.wizardStep) : null;
            return path
                ? [{ label: "Edit in Wizard", icon: <EditIcon fontSize="small" />, onClick: () => navigate(path) }]
                : [];
        },
        renderAddDialog: ({ open, onClose }) => (
            <TripAddDialog
                open={open}
                onClose={onClose}
                dialogTitle={config.dialogTitle}
                wizardStep={config.wizardStep}
            />
        ),
    };
}

const TripEntityList = ({ entityKey }) => {
    const config = useMemo(() => buildTripListConfig(entityKey), [entityKey]);
    // Every key in TRIP_ENTITY_CONFIG is its object's api_name in dash form
    // ("itinerary-day-line-items" -> "itinerary_day_line_items").
    // usePermissionCoverage.test.js checks the result against the registry.
    const permissionObject = entityKey ? entityKey.replace(/-/g, "_") : undefined;
    return <EntityList config={config} permissionObject={permissionObject} />;
};

TripEntityList.propTypes = {
    entityKey: PropTypes.string.isRequired,
};

export default TripEntityList;
